import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Link, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Printer, Pencil, Trash2, Clock, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const inr = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n || 0);

const AGING_BADGE = {
  green: "aging-badge-green",
  yellow: "aging-badge-yellow",
  red: "aging-badge-red",
  critical: "aging-badge-critical",
  paid: "aging-badge-paid",
};

export default function PartialPayments() {
  const [bills, setBills] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState("");
  const [params, setParams] = useSearchParams();
  const [empFilter, setEmpFilter] = useState(params.get("employee") || "all");
  const [dateRange, setDateRange] = useState(params.get("range") || "all");
  const [fromDate, setFromDate] = useState(params.get("from") || "");
  const [toDate, setToDate] = useState(params.get("to") || "");
  const [docType, setDocType] = useState(params.get("doctype") || "all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setParams((p) => {
      const np = new URLSearchParams(p);
      if (empFilter === "all") np.delete("employee");
      else np.set("employee", empFilter);
      return np;
    }, { replace: true });
  }, [empFilter, setParams]);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: b }, { data: e }] = await Promise.all([
        api.get("/bills"),
        api.get("/employees"),
      ]);
      // Show every bill that has been handed to an employee (any follow-up
      // date set). As soon as a user records a "given to employee" date on a
      // bill, it lands on this tab automatically.
      setBills(b.filter((x) => x.days_since_followup !== null && x.days_since_followup !== undefined));
      setEmployees(e);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Move to Recycle Bin?")) return;
    try {
      await api.delete(`/bills/${id}`);
      toast.success("Moved to recycle bin");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const shareOnWhatsApp = (b) => {
    const balance = Math.max(Math.round(b.balance || 0), 0);
    const total = Math.round(b.amount || 0);
    const paid = Math.round(b.paid_amount || 0);
    const label = b.doc_type === "challan" ? "Challan" : "Bill";
    const buyerName = b.buyer?.name || "there";
    const msg =
      `Hi ${buyerName}, gentle reminder — ${label} ${b.bill_number} dated ${b.bill_date} ` +
      `has a pending balance of ₹${balance.toLocaleString("en-IN")}. ` +
      `Total ₹${total.toLocaleString("en-IN")}, Received ₹${paid.toLocaleString("en-IN")}. ` +
      `Kindly settle at the earliest. Thanks!`;
    const raw = (b.buyer?.contact || "").replace(/\D/g, "");
    const phone = raw ? (raw.length === 10 ? `91${raw}` : raw) : "";
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener");
  };

  const filtered = bills
    .filter((b) => (empFilter === "all" ? true : b.employee_id === empFilter))
    .filter((b) => docType === "all" ? true : (b.doc_type || "bill") === docType)
    .filter((b) => {
      if (dateRange === "all") return true;
      const bd = b.bill_date ? new Date(b.bill_date) : null;
      if (!bd || isNaN(bd)) return true;
      if (dateRange === "custom") {
        if (fromDate && bd < new Date(fromDate)) return false;
        if (toDate && bd > new Date(toDate)) return false;
        return true;
      }
      const days = Math.floor((Date.now() - bd.getTime()) / 86400000);
      return days <= parseInt(dateRange, 10);
    })
    .filter((b) => {
      const s = q.trim().toLowerCase();
      if (!s) return true;
      return (
        (b.bill_number || "").toLowerCase().includes(s) ||
        (b.buyer?.name || "").toLowerCase().includes(s) ||
        (b.employee?.name || "").toLowerCase().includes(s)
      );
    });

  const totalPending = filtered.reduce((s, b) => s + Math.max(b.balance, 0), 0);

  // Chase-list summary: pending totals grouped by employee across ALL handed-over bills
  const empSummary = employees
    .map((e) => {
      const list = bills.filter((b) => b.employee_id === e.id);
      const pending = list.reduce((s, b) => s + Math.max(b.balance, 0), 0);
      return { id: e.id, name: e.name, count: list.length, pending };
    })
    .filter((x) => x.count > 0)
    .sort((a, b) => b.pending - a.pending);

  const selectedEmp = employees.find((e) => e.id === empFilter);

  return (
    <div className="p-8 lg:p-14 max-w-[1600px] mx-auto">
      <div className="flex items-end justify-between mb-10 animate-fade-up">
        <div>
          <div className="text-[11px] uppercase tracking-[0.35em] text-neutral-500">
            Handed Over
          </div>
          <h1 className="font-display text-6xl font-bold tracking-tight mt-2">
            {selectedEmp ? `${selectedEmp.name}'s Chase List` : "With Employee"}
          </h1>
          <p className="text-neutral-600 mt-3 max-w-xl">
            {selectedEmp
              ? `All bills currently sitting with ${selectedEmp.name} — hand this list over for the day's collection round.`
              : "Every bill or challan you've handed to an employee for collection lands here the moment you fill in the \"given at which date\" field."}
          </p>
        </div>
        <div className="card-surface p-5 text-right">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">
            Pending across list
          </div>
          <div className="font-mono-num text-3xl font-bold text-[#C4361B] mt-1" data-testid="partial-total-pending">
            {inr(totalPending)}
          </div>
          <div className="text-xs text-neutral-500 mt-1">{filtered.length} bills</div>
        </div>
      </div>

      {/* Chase-list chips — per-employee pending totals */}
      {empSummary.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6 animate-fade-up" data-testid="chase-chip-row">
          <button
            onClick={() => setEmpFilter("all")}
            data-testid="chase-chip-all"
            className={`chase-chip ${empFilter === "all" ? "chase-chip-active" : ""}`}
          >
            <span className="chase-chip-name">Everyone</span>
            <span className="chase-chip-meta">
              {bills.length} <span className="opacity-60">·</span>{" "}
              <span className="font-mono-num">{inr(bills.reduce((s, b) => s + Math.max(b.balance, 0), 0))}</span>
            </span>
          </button>
          {empSummary.map((e) => (
            <button
              key={e.id}
              onClick={() => setEmpFilter(e.id)}
              data-testid={`chase-chip-${e.id}`}
              className={`chase-chip ${empFilter === e.id ? "chase-chip-active" : ""}`}
            >
              <span className="chase-chip-name">{e.name}</span>
              <span className="chase-chip-meta">
                {e.count} <span className="opacity-60">·</span>{" "}
                <span className="font-mono-num">{inr(e.pending)}</span>
              </span>
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-6 animate-fade-up">
        <Input
          placeholder="Search by bill number, buyer, employee…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="hygenix-input max-w-xs"
          data-testid="partial-search"
        />
        <Select value={empFilter} onValueChange={setEmpFilter}>
          <SelectTrigger className="hygenix-input w-52" data-testid="partial-employee-filter">
            <SelectValue placeholder="All employees" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All employees</SelectItem>
            {employees.map((e) => (
              <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex rounded-full border border-neutral-200 bg-white overflow-hidden">
          {[
            ["all", "All time"],
            ["15", "15d"],
            ["30", "30d"],
            ["45", "45d"],
            ["60", "60d"],
            ["custom", "Custom"],
          ].map(([f, label]) => (
            <button
              key={f}
              onClick={() => setDateRange(f)}
              data-testid={`partial-daterange-${f}`}
              className={`px-3 py-2 text-xs uppercase tracking-wider transition-colors ${
                dateRange === f ? "bg-[#EA5B3A] text-white" : "text-neutral-700 hover:bg-neutral-50"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {dateRange === "custom" && (
          <div className="flex items-center gap-2">
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
              className="hygenix-input w-40" data-testid="partial-custom-from" />
            <span className="text-xs text-neutral-500">to</span>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
              className="hygenix-input w-40" data-testid="partial-custom-to" />
          </div>
        )}
        <div className="flex rounded-full border border-neutral-200 bg-white overflow-hidden">
          {[
            ["all", "All docs"],
            ["bill", "Bills"],
            ["challan", "Challans"],
          ].map(([f, label]) => (
            <button
              key={f}
              onClick={() => setDocType(f)}
              data-testid={`partial-doctype-${f}`}
              className={`px-4 py-2 text-xs uppercase tracking-wider transition-colors ${
                docType === f
                  ? f === "challan"
                    ? "bg-[#4238C1] text-white"
                    : f === "bill"
                    ? "bg-[#0F2F1E] text-white"
                    : "bg-neutral-800 text-white"
                  : "text-neutral-700 hover:bg-neutral-50"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3 stagger" data-testid="partial-list">
        {loading && <div className="text-sm text-neutral-500">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="card-surface p-12 text-center">
            <Clock size={22} strokeWidth={1.75} className="mx-auto text-neutral-400 mb-3" />
            <div className="text-neutral-600 font-medium">Nothing handed over yet.</div>
            <div className="text-xs text-neutral-500 mt-1">
              Fill in the &quot;given at which date&quot; field on any bill and
              it will show up here automatically.
            </div>
          </div>
        )}
        {filtered.map((b) => (
          <div
            key={b.id}
            className="card-surface flex items-stretch gap-4 pl-4 pr-4 py-4 overflow-hidden"
            data-testid={`partial-row-${b.bill_number}`}
          >
            <div className={`aging-badge ${AGING_BADGE[b.aging] || "aging-badge-green"} my-1`}>
              <div className="aging-badge-num">{b.days_since ?? 0}</div>
              <div className="aging-badge-unit">day</div>
            </div>
            <div className="aging-badge aging-badge-red"
                 data-testid={`partial-followup-badge-${b.bill_number}`}
                 title="Days since given to employee">
              <div className="aging-badge-num">{b.days_since_followup ?? 0}</div>
              <div className="aging-badge-unit">day/EMP</div>
            </div>

            <div className="flex-1 min-w-0 grid grid-cols-12 items-center gap-3">
              <div className="col-span-12 md:col-span-5 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-mono-num text-sm">{b.bill_number}</div>
                  <span className={b.doc_type === "challan" ? "badge-doctype-challan" : "badge-doctype-bill"}>
                    {b.doc_type === "challan" ? "Challan" : "Bill"}
                  </span>
                </div>
                <div className="text-sm font-semibold truncate mt-0.5">
                  {b.buyer?.name || "—"}
                </div>
                <div className="text-xs text-neutral-500">
                  via {b.employee?.name || "—"} · bill {b.bill_date}
                  {b.follow_up_date ? ` · given ${b.follow_up_date}` : ""}
                </div>
              </div>

              <div className="col-span-12 md:col-span-4">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg py-1.5 px-2 bg-neutral-50">
                    <div className="text-[9px] uppercase tracking-wider text-neutral-500 font-semibold">Total</div>
                    <div className="font-mono-num text-sm font-semibold">{inr(b.amount)}</div>
                  </div>
                  <div className="rounded-lg py-1.5 px-2 bg-[#E8F5EE]">
                    <div className="text-[9px] uppercase tracking-wider text-[#16A34A] font-semibold">Received</div>
                    <div className="font-mono-num text-sm font-semibold text-[#0F6E33]">{inr(b.paid_amount)}</div>
                  </div>
                  <div className="rounded-lg py-1.5 px-2 bg-[#FEE7DF]">
                    <div className="text-[9px] uppercase tracking-wider text-[#C4361B] font-semibold">Pending</div>
                    <div className="font-mono-num text-sm font-semibold text-[#C4361B]">{inr(b.balance)}</div>
                  </div>
                </div>
              </div>

              <div className="col-span-8 md:col-span-1 flex flex-wrap gap-1.5">
                {b.status === "partial" && <span className="badge-partial">Part paid</span>}
              </div>

              <div className="col-span-4 md:col-span-2 flex justify-end items-center gap-1">
                <button onClick={() => shareOnWhatsApp(b)}
                        className="p-2 rounded-lg hover:bg-[#DCFCE7] text-[#16A34A]"
                        title="Share on WhatsApp"
                        data-testid={`partial-whatsapp-${b.bill_number}`}>
                  <MessageCircle size={14} strokeWidth={2} />
                </button>
                <Link to={`/bills/${b.id}/print`} target="_blank"
                      className="p-2 rounded-lg hover:bg-neutral-100 text-neutral-700"
                      data-testid={`partial-print-${b.bill_number}`}>
                  <Printer size={14} strokeWidth={2} />
                </Link>
                <Link to={`/bills/${b.id}/edit`}
                      className="p-2 rounded-lg hover:bg-neutral-100 text-neutral-700"
                      data-testid={`partial-edit-${b.bill_number}`}>
                  <Pencil size={14} strokeWidth={2} />
                </Link>
                <button onClick={() => remove(b.id)}
                        className="p-2 rounded-lg hover:bg-[#FFE5DE] text-[#C4361B]"
                        data-testid={`partial-delete-${b.bill_number}`}>
                  <Trash2 size={14} strokeWidth={2} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
